/**
 * THE PRICE HEADLINE'S WORDS (Miles, 2026-11-02; docs/PLAN-per-employee-pricing-20261030.md, rulings R13a–R13d): the
 * one way the web application and Prest0n say the price. "$700 a month for a full-time employee. unlimited seats.*", what
 * that saves a year on each one against a full-time hire, in green, and the one link to the math. Miles, 2026-11-03: the
 * main messaging says "unlimited seats" (it read "unlimited human seats"), and what it means (people) is said where a
 * visitor asks: the questions, checkout and the details (SEATS_MEANING); the hire's own cost line and the word "modeled"
 * left the drawn block ("too much text", "remove the word modeled": the link opens the model).
 *
 * PURE ON PURPOSE: numbers in, words out, no imports. Its server twin, functions/src/billing/priceHeadlineWords.ts, is
 * this file byte for byte (scripts/gates.d/price-headline-twin.gate), so a page and Prest0n can never say the price two
 * ways (CLAUDE.md, one owner per vocabulary). Each side passes in its own figures (the price ladder and the burden
 * data's generated headline) and its own money format (formatPriceUsd, the one money format, C-15).
 *
 * The words are the site's lowercase voice. "save", "saved" and "saves" mean against a human hire and nothing else (a
 * discount on the ladder reads "off"), so a page never shows two kinds of saving side by side. No hours appear here:
 * hours of work live in the ROI calculation sheets only (R13d).
 */

/** People are free: said beside every price of a Prest0n employee (R13c; "unlimited seats" since 2026-11-03). */
export const UNLIMITED_SEATS = 'unlimited seats';

/** What "unlimited seats" means, where a visitor asks (the questions, checkout, the details): seats are people. */
export const SEATS_MEANING = 'every person on your team uses prest0 at no extra cost; you pay only for Prest0n employees.';

/**
 * The primary offer's count (rulings R2, R9, R13a): three Prest0n employees on an annual commitment billed monthly, the
 * count every picker starts at and every headline prices, for the server (Prest0n's facts). The pages read
 * src/config/priceLadder.ts's; tests/landing/priceHeadline.test.ts holds the two equal.
 */
export const PRIMARY_OFFER_SEATS = 3;

/** The figures the headline is built from, each read from its one home by the caller. */
export interface PriceHeadlineFigures {
  /** The per-employee price the headline quotes: the primary offer's rung, a month. */
  readonly perEmployeeUsd: number;
  /** The count the footnote names (the primary offer's): three. */
  readonly seats: number;
  /** What a full-time hire costs, all overhead in, at the center estimate: a year and a month. */
  readonly hireAnnualUsd: number;
  readonly hireMonthlyUsd: number;
}

/** A money figure, in the caller's one money format. */
export type UsdFormat = (usd: number) => string;

const COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/** A count as the headline says it ("three"); past ten, its digits. */
export function countWord(n: number): string {
  return COUNT_WORDS[n] ?? n.toLocaleString('en-US');
}

/** What one Prest0n employee saves a year against one full-time hire, all overhead in: the hire's year less twelve months. */
export function savingPerEmployeeYearlyUsd(f: Pick<PriceHeadlineFigures, 'perEmployeeUsd' | 'hireAnnualUsd'>): number {
  return f.hireAnnualUsd - 12 * f.perEmployeeUsd;
}

/** What a team saves a year: that many hires' year less the plan's twelve months (a prepaid year's month-equivalent). */
export function teamSavingYearlyUsd(hireAnnualUsd: number, employees: number, planMonthlyUsd: number): number {
  return employees * hireAnnualUsd - 12 * planMonthlyUsd;
}

/** The headline, line by line, as a page draws it (the asterisk is its own part, drawn small). */
export interface PriceHeadline {
  /** "$700 a month for a full-time employee." (the primary offer's rung: $653 on edition 2026-11, $700 from 2026-12) */
  readonly headline: string;
  /** "unlimited seats." */
  readonly seats: string;
  /** The footnote's mark, after the seats line. */
  readonly mark: string;
  /** "a full-time hire costs $11,011 a month, all overhead in." (the sentence and the math say it; the block does not draw it) */
  readonly hire: string;
  /** "$123,733", drawn in green, then its tail (the hire's year less twelve months of the rung). */
  readonly saving: string;
  readonly savingTail: string;
  /** The one link, to the itemized math: "see the math". */
  readonly math: string;
  /** "* each, three on the annual plan, billed monthly." */
  readonly footnote: string;
  /** The whole thing as one sentence, for search, machine files and Prest0n. */
  readonly sentence: string;
}

export function priceHeadline(f: PriceHeadlineFigures, usd: UsdFormat): PriceHeadline {
  const price = usd(f.perEmployeeUsd);
  const saving = usd(savingPerEmployeeYearlyUsd(f));
  const hire = `a full-time hire costs ${usd(f.hireMonthlyUsd)} a month, all overhead in.`;
  const footnote = `* each, ${countWord(f.seats)} on the annual plan, billed monthly.`;
  return {
    headline: `${price} a month for a full-time employee.`,
    seats: `${UNLIMITED_SEATS}.`,
    mark: '*',
    hire,
    saving,
    savingTail: 'saved a year on each one',
    math: 'see the math',
    footnote,
    sentence:
      `${price} a month for a full-time employee (each, ${countWord(f.seats)} on the annual plan, billed monthly), with ${UNLIMITED_SEATS}: ${SEATS_MEANING} ` +
      `${hire.slice(0, -1)}: ${saving} saved a year on each one, modeled.`,
  };
}

/** After a team's saving, as the hire card says it: "$372,891 saved a year vs 3 full-time hires". */
export function teamSavingTail(employees: number): string {
  return `saved a year vs ${employees.toLocaleString('en-US')} full-time ${employees === 1 ? 'hire' : 'hires'}`;
}

/** A price line's seats clause, for every place that states a Prest0n employee's price (R13c). */
export function withSeats(priceLine: string): string {
  return `${priceLine} · ${UNLIMITED_SEATS}`;
}
